
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AdBanner from '../../components/feature/AdBanner';
import FeedbackBanner from '../../components/feature/FeedbackBanner';

export default function ArticleGestaoAtivosFixos() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <SEOHead 
        title="Gestão de Ativos Fixos: Controle Patrimonial Eficiente para Empresas | Guia Completo 2025"
        description="Guia completo sobre gestão de ativos fixos e controle patrimonial: inventário, conciliação físico-contábil, depreciação, tecnologia e boas práticas para empresas e contadores."
        keywords="gestão de ativos fixos, controle patrimonial, ativo imobilizado, depreciação, inventário físico, conciliação contábil, gestão patrimonial, balanço patrimonial, auditoria"
        canonical={`https://ativonews.com.br/artigo/gestao-ativos-fixos-controle-patrimonial`}
      />
      <SchemaOrg type="Article" data={{
        headline: "Gestão de Ativos Fixos: Controle Patrimonial Eficiente",
        description: "Guia completo sobre gestão de ativos fixos e controle patrimonial eficiente para empresas",
        author: "Dr. Fernando Oliveira",
        datePublished: "2025-01-15",
        image: "https://public.readdy.ai/ai/img_res/f864841c5dd288fc74e2dcb96ff4f60b.jpg"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
          <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
              <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <Link to="/categoria/gestao-empresarial" className="hover:text-[#1E40AF] transition-colors">
                Gestão Empresarial
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-[#0F172A] font-medium">Gestão de Ativos Fixos</span>
            </nav>

            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-[#8B5CF6] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                Gestão Empresarial
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
              Gestão de Ativos Fixos: Controle Patrimonial Eficiente
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
              Guia completo para estruturar um controle patrimonial eficiente, garantindo confiabilidade contábil, conformidade regulatória e apoio estratégico à tomada de decisão.
            </p>

            {/* Author & Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-gray-400"></i>
                </div>
                <div>
                  <div className="font-semibold text-[#0F172A]">Dr. Fernando Oliveira</div>
                  <div className="text-sm">Especialista em Gestão Patrimonial e Controle de Ativos</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>15 de janeiro de 2025</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>11 min</span>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="w-full h-[500px] bg-gray-100">
          <img 
            src="https://readdy.ai/api/search-image?query=modern%20warehouse%20asset%20management%20system%20professional%20inventory%20control%20equipment%20machinery%20organization%20industrial%20facility%20clean%20organized%20storage%20professional%20workplace%20business%20operations%20technology%20integration%20high%20quality%20photography&width=1200&height=500&seq=gestao-ativos-featured&orientation=landscape"
            alt="Gestão de Ativos Fixos"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Content Area */}
        <div className="w-full bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12">
              
              {/* Main Content */}
              <article className="lg:col-span-8">
                <div className="prose prose-lg max-w-none">
                  
                  {/* TL;DR */}
                  <div className="bg-purple-50 border-l-4 border-purple-600 p-6 mb-10 rounded-r-lg">
                    <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-flashlight-line text-purple-600"></i>
                      TL;DR — Resumo Executivo
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      A gestão de ativos fixos é fundamental para a <strong>saúde financeira e contábil</strong> das empresas. Um controle patrimonial eficiente garante informações confiáveis no balanço, reduz riscos fiscais, facilita auditorias e apoia decisões estratégicas. Este guia apresenta os pilares do controle patrimonial: inventário físico, conciliação físico-contábil, classificação e depreciação corretas, controle de movimentações e uso de tecnologia. Empresas que investem em gestão patrimonial estruturada minimizam perdas, aumentam transparência e transformam o ativo imobilizado em um <strong>aliado estratégico</strong>.
                    </p>
                  </div>

                  {/* Introdução */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Introdução</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A gestão de ativos fixos é um dos pilares para a saúde financeira, contábil e operacional das empresas. Um controle patrimonial ineficiente pode gerar distorções contábeis, perdas financeiras, riscos fiscais e dificuldades na tomada de decisão.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Neste artigo, você vai entender o que é gestão de ativos fixos, por que ela é essencial e como estruturar um controle patrimonial eficiente, alinhado às boas práticas contábeis e às exigências regulatórias.
                    </p>
                  </section>

                  {/* O que são ativos fixos */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">O que são ativos fixos?</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Ativos fixos são bens tangíveis utilizados pela empresa para a manutenção de suas atividades e que não se destinam à venda, como:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-blue-50 p-5 rounded-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-tools-line text-blue-600"></i>
                          Máquinas e Equipamentos
                        </h3>
                        <p className="text-sm text-gray-700">Equipamentos industriais e operacionais</p>
                      </div>
                      <div className="bg-green-50 p-5 rounded-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-building-line text-green-600"></i>
                          Imóveis
                        </h3>
                        <p className="text-sm text-gray-700">Terrenos, prédios e instalações</p>
                      </div>
                      <div className="bg-orange-50 p-5 rounded-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-car-line text-orange-600"></i>
                          Veículos
                        </h3>
                        <p className="text-sm text-gray-700">Frota operacional e administrativa</p>
                      </div>
                      <div className="bg-purple-50 p-5 rounded-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-computer-line text-purple-600"></i>
                          Móveis e TI
                        </h3>
                        <p className="text-sm text-gray-700">Mobiliário e equipamentos de informática</p>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      Esses bens compõem o <strong>ativo imobilizado</strong> e impactam diretamente o balanço patrimonial e os resultados da empresa.
                    </p>
                  </section>

                  {/* O que é gestão de ativos fixos */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">O que é gestão de ativos fixos?</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Gestão de ativos fixos é o conjunto de processos que envolvem:
                    </p>
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-8">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Identificação e Registro</h3>
                            <p className="text-sm text-gray-700">Cadastro completo de todos os bens</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Controle Físico e Contábil</h3>
                            <p className="text-sm text-gray-700">Rastreamento e conciliação constante</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Depreciação e Vida Útil</h3>
                            <p className="text-sm text-gray-700">Cálculo correto da depreciação</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Movimentações Internas</h3>
                            <p className="text-sm text-gray-700">Transferências e remanejamentos</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Baixas e Alienações</h3>
                            <p className="text-sm text-gray-700">Controle de vendas e perdas</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Conciliação</h3>
                            <p className="text-sm text-gray-700">Alinhamento físico-contábil</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed mt-6 font-semibold">
                        O objetivo é garantir <strong>informações confiáveis, rastreabilidade e conformidade</strong>.
                      </p>
                    </div>
                  </section>

                  {/* Por que é importante */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Por que o controle patrimonial eficiente é tão importante?</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      Um controle patrimonial bem estruturado permite:
                    </p>
                    <div className="space-y-4">
                      <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-check-double-line text-blue-600"></i>
                          Confiabilidade das Demonstrações Contábeis
                        </h3>
                        <p className="text-gray-700 text-sm">Balanço patrimonial e DRE com dados precisos e auditáveis</p>
                      </div>
                      <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-shield-check-line text-green-600"></i>
                          Redução de Riscos Fiscais e Societários
                        </h3>
                        <p className="text-gray-700 text-sm">Conformidade com normas contábeis e tributárias</p>
                      </div>
                      <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-line-chart-line text-purple-600"></i>
                          Apoio à Tomada de Decisão Gerencial
                        </h3>
                        <p className="text-gray-700 text-sm">Informações confiáveis para investimentos e estratégia</p>
                      </div>
                      <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-search-eye-line text-orange-600"></i>
                          Atendimento a Auditorias
                        </h3>
                        <p className="text-gray-700 text-sm">Documentação organizada e rastreabilidade completa</p>
                      </div>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mt-6">
                      <p className="text-gray-700">
                        <strong>⚠️ Atenção:</strong> Empresas sem controle adequado costumam descobrir problemas apenas em fiscalizações ou auditorias, quando o custo de correção é maior.
                      </p>
                    </div>
                  </section>

                  {/* Principais falhas */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Principais falhas no controle de ativos fixos</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      Alguns erros são recorrentes e geram graves consequências:
                    </p>
                    <div className="space-y-4">
                      <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2">❌ Ativos registrados que não existem fisicamente</h3>
                        <p className="text-gray-700 text-sm">Distorção patrimonial e risco fiscal de depreciação indevida</p>
                      </div>
                      <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2">❌ Bens em uso sem registro contábil</h3>
                        <p className="text-gray-700 text-sm">Subavaliação do patrimônio e perda de controle operacional</p>
                      </div>
                      <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2">❌ Depreciação incorreta</h3>
                        <p className="text-gray-700 text-sm">Impacto direto no resultado e no IR/CS</p>
                      </div>
                      <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2">❌ Falta de controle de movimentações</h3>
                        <p className="text-gray-700 text-sm">Ativos perdidos, extraviados ou sem responsável definido</p>
                      </div>
                      <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <h3 className="font-bold text-gray-900 mb-2">❌ Ausência de inventários periódicos</h3>
                        <p className="text-gray-700 text-sm">Descontrole crescente e surpresas em auditorias</p>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mt-6">
                      Essas falhas geram <strong>distorções patrimoniais e riscos regulatórios</strong> significativos.
                    </p>
                  </section>

                  {/* Como estruturar */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Como estruturar um controle patrimonial eficiente</h2>
                    
                    {/* 1. Inventário */}
                    <div className="bg-white border-2 border-blue-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-list-3-line text-2xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">1. Inventário Físico dos Ativos</h3>
                          <p className="text-gray-700 mb-4">
                            O primeiro passo é identificar e validar fisicamente todos os bens:
                          </p>
                          <div className="bg-blue-50 p-4 rounded-lg mb-4">
                            <ul className="space-y-2">
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-map-pin-line text-blue-600 mt-0.5"></i>
                                <span><strong>Localização:</strong> Onde o bem está fisicamente</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-checkbox-circle-line text-blue-600 mt-0.5"></i>
                                <span><strong>Estado de conservação:</strong> Condições de uso</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-user-line text-blue-600 mt-0.5"></i>
                                <span><strong>Responsável:</strong> Quem responde pelo bem</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-barcode-line text-blue-600 mt-0.5"></i>
                                <span><strong>Número patrimonial:</strong> Identificação única</span>
                              </li>
                            </ul>
                          </div>
                          <p className="text-gray-700 font-semibold">
                            O inventário físico é a <strong>base de todo o processo</strong>.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 2. Conciliação */}
                    <div className="bg-white border-2 border-green-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-git-merge-line text-2xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">2. Conciliação Físico-Contábil</h3>
                          <p className="text-gray-700 mb-4">
                            Após o inventário, é essencial conciliar:
                          </p>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-green-50 p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2">✓ O que existe fisicamente</h4>
                              <p className="text-sm text-gray-700">Resultado do inventário</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2">✓ O que está registrado</h4>
                              <p className="text-sm text-gray-700">Dados na contabilidade</p>
                            </div>
                          </div>
                          <p className="text-gray-700 mt-4">
                            <strong>Diferenças devem ser analisadas, justificadas e tratadas corretamente.</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 3. Classificação */}
                    <div className="bg-white border-2 border-purple-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-price-tag-3-line text-2xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">3. Classificação Correta e Vida Útil Adequada</h3>
                          <p className="text-gray-700 mb-4">
                            Cada ativo deve:
                          </p>
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <ul className="space-y-2">
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-checkbox-circle-fill text-purple-600 mt-0.5"></i>
                                <span>Estar corretamente classificado (natureza do bem)</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-checkbox-circle-fill text-purple-600 mt-0.5"></i>
                                <span>Ter vida útil compatível com sua utilização</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-checkbox-circle-fill text-purple-600 mt-0.5"></i>
                                <span>Seguir critérios contábeis e fiscais aplicáveis</span>
                              </li>
                            </ul>
                          </div>
                          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-4">
                            <p className="text-sm text-gray-700">
                              <strong>Importante:</strong> A depreciação impacta diretamente o resultado da empresa.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 4. Movimentações */}
                    <div className="bg-white border-2 border-orange-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-orange-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-left-right-line text-2xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">4. Controle de Movimentações</h3>
                          <p className="text-gray-700 mb-4">
                            Transferências, mudanças de local, cessões e baixas precisam ser:
                          </p>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-orange-50 p-4 rounded-lg text-center">
                              <i className="ri-file-edit-line text-3xl text-orange-600 mb-2"></i>
                              <h4 className="font-bold text-gray-900">Registradas</h4>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg text-center">
                              <i className="ri-checkbox-line text-3xl text-orange-600 mb-2"></i>
                              <h4 className="font-bold text-gray-900">Aprovadas</h4>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg text-center">
                              <i className="ri-file-text-line text-3xl text-orange-600 mb-2"></i>
                              <h4 className="font-bold text-gray-900">Documentadas</h4>
                            </div>
                          </div>
                          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-4">
                            <p className="text-sm text-gray-700">
                              <strong>⚠️ Alerta:</strong> Sem controle de movimentação, o inventário se deteriora rapidamente.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 5. Tecnologia */}
                    <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-cloud-line text-2xl"></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">5. Uso de Tecnologia no Controle Patrimonial</h3>
                          <p className="text-gray-700 mb-4">
                            Ferramentas de gestão patrimonial permitem:
                          </p>
                          <div className="bg-teal-50 p-4 rounded-lg">
                            <ul className="space-y-2">
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-database-2-line text-teal-600 mt-0.5"></i>
                                <span><strong>Registro centralizado</strong> dos ativos</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-roadmap-line text-teal-600 mt-0.5"></i>
                                <span><strong>Rastreabilidade</strong> completa de movimentações</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-speed-up-line text-teal-600 mt-0.5"></i>
                                <span><strong>Inventários mais rápidos</strong> e precisos</span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-error-warning-line text-teal-600 mt-0.5"></i>
                                <span><strong>Redução de erros manuais</strong></span>
                              </li>
                              <li className="flex items-start gap-2 text-sm text-gray-700">
                                <i className="ri-links-line text-teal-600 mt-0.5"></i>
                                <span><strong>Integração</strong> com sistemas contábeis e ERPs</span>
                              </li>
                            </ul>
                          </div>
                          <p className="text-gray-700 mt-4 font-semibold">
                            A tecnologia <strong>aumenta a confiabilidade e reduz custos operacionais</strong>.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Conformidade contábil */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Gestão de ativos fixos e conformidade contábil</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A correta gestão patrimonial é fundamental para:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-2xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Atender Normas Contábeis</h3>
                            <p className="text-sm text-gray-700">CPC, NBC e legislação aplicável</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-2xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Suportar Auditorias</h3>
                            <p className="text-sm text-gray-700">Internas e externas com segurança</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-2xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Garantir Transparência</h3>
                            <p className="text-sm text-gray-700">Nas demonstrações financeiras</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-2xl mt-0.5"></i>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Reduzir Riscos</h3>
                            <p className="text-sm text-gray-700">De ajustes e contingências</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed mt-6 font-semibold">
                        Empresas com controle patrimonial eficiente apresentam <strong>menor risco de ajustes e contingências</strong>.
                      </p>
                    </div>
                  </section>

                  {/* Benefícios */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Benefícios de uma gestão de ativos bem estruturada</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                        <i className="ri-checkbox-double-line text-4xl text-green-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Informações Patrimoniais Confiáveis</h3>
                        <p className="text-gray-700 text-sm">Balanço e relatórios com dados precisos e auditáveis</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
                        <i className="ri-shield-check-line text-4xl text-blue-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Redução de Perdas e Extravios</h3>
                        <p className="text-gray-700 text-sm">Controle rigoroso minimiza perdas patrimoniais</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200">
                        <i className="ri-line-chart-line text-4xl text-purple-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Melhor Aproveitamento dos Bens</h3>
                        <p className="text-gray-700 text-sm">Otimização do uso e manutenção dos ativos</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl border-2 border-orange-200">
                        <i className="ri-lightbulb-line text-4xl text-orange-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Apoio à Estratégia Empresarial</h3>
                        <p className="text-gray-700 text-sm">Decisões de investimento mais assertivas</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-xl border-2 border-red-200">
                        <i className="ri-shield-star-line text-4xl text-red-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Maior Segurança em Fiscalizações</h3>
                        <p className="text-gray-700 text-sm">Documentação organizada e rastreável</p>
                      </div>
                      <div className="bg-gradient-to-br from-teal-50 to-green-50 p-6 rounded-xl border-2 border-teal-200">
                        <i className="ri-trophy-line text-4xl text-teal-600 mb-3"></i>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Vantagem Competitiva</h3>
                        <p className="text-gray-700 text-sm">Diferenciação pela excelência na gestão</p>
                      </div>
                    </div>
                  </section>

                  {/* Conclusão */}
                  <section className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusão</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A gestão de ativos fixos vai muito além do registro contábil. Ela exige <strong>integração entre áreas, processos bem definidos e uso de tecnologia</strong> para garantir controle patrimonial eficiente e sustentável.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Empresas que investem em uma boa gestão patrimonial reduzem riscos, aumentam a transparência e tomam decisões mais seguras, transformando o ativo imobilizado em um <strong>verdadeiro aliado estratégico</strong>.
                    </p>
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-8 mt-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-star-line text-purple-600"></i>
                        Próximos Passos Recomendados:
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <i className="ri-arrow-right-circle-fill text-purple-600 text-xl mt-0.5"></i>
                          <span className="text-gray-700"><strong>Realize um inventário físico completo</strong> de todos os ativos</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-arrow-right-circle-fill text-purple-600 text-xl mt-0.5"></i>
                          <span className="text-gray-700"><strong>Concilie físico-contábil</strong> e corrija divergências</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-arrow-right-circle-fill text-purple-600 text-xl mt-0.5"></i>
                          <span className="text-gray-700"><strong>Implemente sistema de gestão patrimonial</strong></span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-arrow-right-circle-fill text-purple-600 text-xl mt-0.5"></i>
                          <span className="text-gray-700"><strong>Estabeleça rotinas de controle</strong> e inventários periódicos</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* Sobre o Autor */}
                  <div className="mt-16 p-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-user-star-line text-purple-600"></i>
                      Sobre o Autor
                    </h3>
                    <div className="flex items-start gap-6">
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                        <i className="ri-user-line text-4xl text-gray-400"></i>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">Dr. Fernando Oliveira</h4>
                        <p className="text-gray-700 leading-relaxed mb-3">
                          Especialista em Gestão Patrimonial e Controle de Ativos com mais de 15 anos de experiência. Mestre em Ciências Contábeis pela USP, com foco em gestão de ativos, auditoria e conformidade contábil. Consultor de empresas de médio e grande porte, palestrante e autor de artigos sobre controle patrimonial, inventário físico e boas práticas contábeis.
                        </p>
                        <p className="text-gray-700 text-sm">
                          <strong>Formação:</strong> Mestrado em Ciências Contábeis (USP), MBA em Gestão Empresarial, Graduação em Contabilidade<br />
                          <strong>Certificações:</strong> CRC-SP, Auditor Independente, Especialista em Normas Internacionais de Contabilidade
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-8">
                  
                  {/* Navegação do Artigo */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <i className="ri-list-check text-purple-600"></i>
                      Neste Artigo
                    </h3>
                    <ul className="space-y-2 text-sm">
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → O que são ativos fixos
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → O que é gestão de ativos fixos
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → Por que é importante
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → Principais falhas no controle
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → Como estruturar controle eficiente
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → Conformidade contábil
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-gray-600 hover:text-purple-600 transition-colors">
                          → Benefícios
                        </a>
                      </li>
                    </ul>
                  </div>

                  {/* Ad Banner */}
                  <AdBanner position="vertical" size="medium" />

                  {/* Author Info */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        <i className="ri-user-line text-2xl text-gray-400"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">Dr. Fernando Oliveira</h4>
                        <p className="text-sm text-gray-600">Especialista em Gestão Patrimonial</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">
                      Mais de 15 anos de experiência em gestão de ativos fixos, auditoria e conformidade contábil.
                    </p>
                    <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                      Ver mais artigos
                    </button>
                  </div>

                  {/* Ad Banner */}
                  <AdBanner position="square" size="small" />

                  {/* Tags */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Ativos Fixos</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Gestão Patrimonial</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Inventário</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Depreciação</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Controle</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">Auditoria</span>
                    </div>
                  </div>

                </div>
              </aside>

            </div>
          </div>
        </div>

        {/* Ad Banner before Feedback */}
        <div className="w-full bg-[#F8FAFC] py-8">
          <div className="max-w-7xl mx-auto px-6">
            <AdBanner position="horizontal" size="large" />
          </div>
        </div>

        {/* Feedback Banner */}
        <div className="w-full bg-white">
          <FeedbackBanner 
            articleTitle="Gestão de Ativos Fixos: Controle Patrimonial Eficiente"
            articleUrl={articleUrl}
          />
        </div>

      </div>
      
      <Footer />
    </>
  );
}
